import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { hrmApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'catalog' | 'my_training' | 'assignments' | 'compliance';

/** FR-HR-07 — Training */
export function TrainingPage() {
  const [tab, setTab] = useState<Tab>('catalog');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'catalog',     label: 'Catalog' },
    { key: 'my_training', label: 'My training' },
    { key: 'assignments', label: 'Assignments' },
    { key: 'compliance',  label: 'Compliance' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="People"
        title="Training"
        subtitle="Course catalog, assignments, completion tracking, and mandatory-training compliance."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'catalog' && <CatalogTab />}
      {tab === 'my_training' && <MyTrainingTab />}
      {tab === 'assignments' && <AssignmentsTab />}
      {tab === 'compliance' && <ComplianceTab />}
    </div>
  );
}

function CatalogTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [assignFromCourse, setAssignFromCourse] = useState<any | null>(null);
  const [archivingCourse, setArchivingCourse] = useState<any | null>(null);

  const q = useQuery({
    queryKey: ['training-courses'],
    queryFn: () => hrmApi.listCourses(),
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => hrmApi.updateCourse(id, { status: 'archived' }),
    onSuccess: () => {
      toast.success('Archived');
      qc.invalidateQueries({ queryKey: ['training-courses'] });
      setArchivingCourse(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteCourseMut = useMutation({
    mutationFn: (id: string) => hrmApi.deleteCourse(id),
    onSuccess: () => { toast.success('Course deleted'); qc.invalidateQueries({ queryKey: ['training-courses'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let active = 0, mandatory = 0, totalAssignments = 0, totalCompleted = 0;
    for (const c of rows) {
      if (c.status === 'active') active++;
      if (c.is_mandatory) mandatory++;
      totalAssignments += c.assignment_count ?? 0;
      totalCompleted += c.completed_count ?? 0;
    }
    const pct = totalAssignments > 0 ? (totalCompleted / totalAssignments) * 100 : 0;
    return { total: rows.length, active, mandatory, pct };
  })();

  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Courses" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent="sage" />
        <KpiTile label="Mandatory" value={kpis.mandatory}
          accent={kpis.mandatory > 0 ? 'danger' : 'default'} />
        <KpiTile label="Completion rate" value={`${kpis.pct.toFixed(0)}%`}
          accent={kpis.pct >= 80 ? 'sage' : kpis.pct >= 50 ? 'amber' : 'default'} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New course</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No training courses yet"
              hint="Add your first course — it can be internal, external, or a video/reading assignment."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New course</button>
              }
            />
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(q.data ?? []).map((c: any) => {
            const pct = c.assignment_count > 0 ? (c.completed_count / c.assignment_count) * 100 : 0;
            return (
              <div key={c.id} className="card p-5">
                <div className="flex justify-between items-start gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-2xs text-ink-500">{c.code}</div>
                    <div className="font-display font-semibold text-ink-900 truncate mt-0.5">{c.title}</div>
                    {c.provider && (
                      <div className="text-2xs text-ink-500 mt-0.5">{c.provider}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 items-end flex-shrink-0">
                    {c.is_mandatory && (
                      <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200">
                        mandatory
                      </span>
                    )}
                    {c.status === 'archived' && (
                      <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-500 ring-ink-200">
                        archived
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap text-2xs text-ink-600 mb-3">
                  {c.category && <span className="px-1.5 py-0.5 bg-canvas-subtle rounded">{c.category}</span>}
                  {c.delivery_mode && <span className="px-1.5 py-0.5 bg-canvas-subtle rounded">{c.delivery_mode.replace('_', ' ')}</span>}
                  {c.duration_hours && <span className="px-1.5 py-0.5 bg-canvas-subtle rounded font-mono tabular-nums">{parseFloat(c.duration_hours).toFixed(1)}h</span>}
                  {c.passing_score && <span className="px-1.5 py-0.5 bg-canvas-subtle rounded font-mono tabular-nums">pass {parseFloat(c.passing_score).toFixed(0)}%</span>}
                </div>

                {c.description && (
                  <div className="text-xs text-ink-600 mb-3 line-clamp-2">{c.description}</div>
                )}

                <div className="mb-3">
                  <div className="flex justify-between text-2xs text-ink-500 mb-1">
                    <span className="font-mono tabular-nums">{c.completed_count} of {c.assignment_count} completed</span>
                    <span className="font-mono font-semibold tabular-nums">{pct.toFixed(0)}%</span>
                  </div>
                  <div className="h-1.5 bg-canvas-subtle rounded-full overflow-hidden">
                    <div
                      className={`h-full ${pct === 100 ? 'bg-sage-500' : pct >= 50 ? 'bg-sage-500' : pct > 0 ? 'bg-amber-500' : 'bg-ink-200'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-3 border-t border-ink-100 items-center">
                  {c.external_url && (
                    <a href={c.external_url} target="_blank" rel="noreferrer"
                      className="text-xs font-medium text-sage-700 hover:text-sage-800 transition-colors">
                      View ↗
                    </a>
                  )}
                  {c.status === 'active' && (
                    <>
                      <button
                        className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors ml-auto"
                        onClick={() => setAssignFromCourse(c)}
                      >
                        Assign
                      </button>
                      <button
                        className="text-xs font-medium text-ink-500 hover:bg-canvas-subtle px-2 py-1 rounded transition-colors"
                        onClick={() => setArchivingCourse(c)}
                      >
                        Archive
                      </button>
                      <button
                        className="text-xs font-medium text-ink-400 hover:text-danger hover:bg-danger-50 px-2 py-1 rounded transition-colors"
                        title="Delete course"
                        onClick={() => { if (confirm(`Delete course ${c.code}? This cannot be undone.`)) deleteCourseMut.mutate(c.id); }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateCourseModal onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['training-courses'] }); setShowCreate(false); }} />}
      {assignFromCourse && <AssignModal course={assignFromCourse}
        onClose={() => setAssignFromCourse(null)}
        onAssigned={() => { qc.invalidateQueries({ queryKey: ['training-courses'] });
                           qc.invalidateQueries({ queryKey: ['training-assignments'] });
                           setAssignFromCourse(null); }} />}
      {archivingCourse && (
        <ArchiveCourseDialog
          course={archivingCourse}
          loading={archiveMut.isPending}
          onClose={() => setArchivingCourse(null)}
          onConfirm={() => archiveMut.mutate(archivingCourse.id)}
        />
      )}
    </div>
  );
}

function ArchiveCourseDialog({
  course, loading, onClose, onConfirm,
}: { course: any; loading: boolean; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">People · Archive</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Archive course?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Archive <span className="font-mono font-semibold text-ink-900">{course.code}</span>
          {' '}— <span className="text-ink-700">{course.title}</span>?
          Existing assignments remain, but the course won't appear in the active catalogue
          and no new assignments can be created.
        </p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? 'Archiving…' : 'Archive course'}
        </button>
      </div>
    </Modal>
  );
}

function MyTrainingTab() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['training-assignments-mine'],
    queryFn: () => hrmApi.listAssignments(),
  });

  const startMut = useMutation({
    mutationFn: (id: string) => hrmApi.startAssignment(id),
    onSuccess: () => { toast.success('Started'); qc.invalidateQueries({ queryKey: ['training-assignments-mine'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const [completing, setCompleting] = useState<any | null>(null);

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div className="space-y-2">
      {!q.data?.length ? (
        <div className="card p-4"><EmptyState message="No training assigned to you." /></div>
      ) : (q.data ?? []).map((a: any) => (
        <div key={a.id} className="card p-4">
          <div className="flex justify-between items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-mono text-xs font-semibold text-ink-700">{a.course_code}</span>
                <AssignmentStatusBadge status={a.display_status} />
                {a.is_mandatory && (
                  <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200">
                    mandatory
                  </span>
                )}
              </div>
              <div className="font-semibold text-ink-900">{a.course_title}</div>
              <div className="text-xs text-ink-500 mt-1">
                {a.duration_hours && (
                  <><span className="font-mono tabular-nums">{parseFloat(a.duration_hours).toFixed(1)}h</span><span className="mx-1.5 text-ink-300">·</span></>
                )}
                <span className="capitalize">{a.delivery_mode?.replace('_', ' ')}</span>
                {a.due_date && (
                  <>
                    <span className="mx-1.5 text-ink-300">·</span>
                    due {new Date(a.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </>
                )}
              </div>
              {a.passed === false && (
                <div className="text-xs text-danger mt-1.5 font-medium">
                  Failed with {parseFloat(a.score).toFixed(0)}% (need {parseFloat(a.passing_score).toFixed(0)}%)
                </div>
              )}
              {a.passed === true && a.score && (
                <div className="text-xs text-sage-700 mt-1.5 font-medium">
                  Passed with {parseFloat(a.score).toFixed(0)}%
                </div>
              )}
            </div>
            <div className="flex flex-col gap-1.5 items-end flex-shrink-0">
              {a.status === 'assigned' && (
                <button className="btn-primary text-xs py-1" disabled={startMut.isPending}
                  onClick={() => startMut.mutate(a.id)}>
                  {startMut.isPending ? 'Starting…' : 'Start'}
                </button>
              )}
              {a.status === 'in_progress' && (
                <button className="btn-primary text-xs py-1" onClick={() => setCompleting(a)}>
                  Complete
                </button>
              )}
              {a.certificate_url && (
                <a href={a.certificate_url} target="_blank" rel="noreferrer"
                  className="text-xs font-semibold text-sage-700 hover:text-sage-800 transition-colors">
                  Certificate →
                </a>
              )}
            </div>
          </div>
        </div>
      ))}

      {completing && <CompleteAssignmentModal assignment={completing}
        onClose={() => setCompleting(null)}
        onCompleted={() => { qc.invalidateQueries({ queryKey: ['training-assignments-mine'] }); setCompleting(null); }} />}
    </div>
  );
}

function AssignmentsTab() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [cancellingAssignment, setCancellingAssignment] = useState<any | null>(null);
  const q = useQuery({
    queryKey: ['training-assignments-all', statusFilter],
    queryFn: () => hrmApi.listAssignments(statusFilter ? { status: statusFilter } : undefined),
  });

  const cancelMut = useMutation({
    mutationFn: ({ id, reason }: any) => hrmApi.cancelAssignment(id, reason),
    onSuccess: () => {
      toast.success('Cancelled');
      qc.invalidateQueries({ queryKey: ['training-assignments-all'] });
      setCancellingAssignment(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <div className="flex gap-2 mb-4 items-center">
        <select className="input max-w-xs" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="assigned">Assigned</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
       q.error ? <ErrorState error={q.error} /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Course</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th className="text-right">Score</th>
                  <th>Completed</th>
                  <th className="text-right w-20"></th>
                </tr>
              </thead>
              <tbody>
                {!q.data?.length ? (
                  <tr><td colSpan={7}><EmptyState message="No assignments." /></td></tr>
                ) : (q.data ?? []).map((a: any) => (
                  <tr key={a.id}>
                    <td>
                      <div className="text-sm font-medium text-ink-900">{a.employee_name}</div>
                      <div className="text-2xs text-ink-500 font-mono mt-0.5">{a.employee_code}</div>
                    </td>
                    <td>
                      <div className="text-sm text-ink-900">{a.course_title}</div>
                      <div className="text-2xs text-ink-500 mt-0.5">
                        <span className="font-mono">{a.course_code}</span>
                        {a.is_mandatory && (
                          <span className="ml-2 inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-1.5 py-0 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200">
                            mandatory
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-2xs text-ink-500 font-mono tabular-nums">
                      {a.due_date
                        ? new Date(a.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : <span className="text-ink-400">—</span>}
                    </td>
                    <td><AssignmentStatusBadge status={a.display_status} /></td>
                    <td className="text-right">
                      {a.score !== null && a.score !== undefined
                        ? <span className={`font-mono font-semibold tabular-nums text-xs ${a.passed ? 'text-sage-700' : 'text-danger'}`}>
                            {parseFloat(a.score).toFixed(0)}%
                          </span>
                        : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-2xs text-ink-500 font-mono tabular-nums">
                      {a.completed_at
                        ? new Date(a.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-right">
                      {!['completed', 'cancelled'].includes(a.status) && (
                        <button
                          className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                          onClick={() => setCancellingAssignment(a)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {cancellingAssignment && (
        <CancelAssignmentDialog
          assignment={cancellingAssignment}
          loading={cancelMut.isPending}
          onClose={() => setCancellingAssignment(null)}
          onConfirm={(reason) => cancelMut.mutate({ id: cancellingAssignment.id, reason })}
        />
      )}
    </div>
  );
}

function CancelAssignmentDialog({
  assignment, loading, onClose, onConfirm,
}: { assignment: any; loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">People · Cancel</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Cancel assignment?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Cancel <span className="font-mono font-semibold text-ink-900">{assignment.course_code}</span>
          {' '}for <span className="text-ink-700">{assignment.employee_name}</span>?
          If the course is mandatory you may want to reassign later. Progress is lost.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. No longer required for role" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep assignment</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={() => onConfirm(reason.trim() || undefined)}
        >
          {loading ? 'Cancelling…' : 'Cancel assignment'}
        </button>
      </div>
    </Modal>
  );
}

function ComplianceTab() {
  const q = useQuery({
    queryKey: ['training-compliance'],
    queryFn: () => hrmApi.getTrainingCompliance(),
  });

  if (q.isLoading) return <div className="card"><LoadingState rows={4} /></div>;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-xs text-amber-900 leading-relaxed">
        Shows compliance for courses marked as mandatory. Assigned vs completed vs overdue, scoped to active employees only.
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Course</th>
                <th className="text-right">Active employees</th>
                <th className="text-right">Assigned</th>
                <th className="text-right">Completed</th>
                <th className="text-right">Overdue</th>
                <th className="text-right w-36">Completion</th>
              </tr>
            </thead>
            <tbody>
              {!q.data?.length ? (
                <tr><td colSpan={6}><EmptyState message="No mandatory courses." /></td></tr>
              ) : (q.data ?? []).map((r: any) => {
                const pct = r.assignedCount > 0 ? (r.completedCount / r.assignedCount) * 100 : 0;
                const coverage = r.totalActiveEmployees > 0 ? (r.assignedCount / r.totalActiveEmployees) * 100 : 0;
                return (
                  <tr key={r.courseId}>
                    <td>
                      <div className="text-sm font-semibold text-ink-900">{r.courseTitle}</div>
                      <div className="text-2xs text-ink-500 font-mono mt-0.5">{r.courseCode}</div>
                    </td>
                    <td className="text-right font-mono tabular-nums text-sm">{r.totalActiveEmployees}</td>
                    <td className="text-right">
                      <span className="font-mono font-semibold tabular-nums text-sm">{r.assignedCount}</span>
                      <span className="text-2xs text-ink-400 ml-1 font-mono tabular-nums">({coverage.toFixed(0)}%)</span>
                    </td>
                    <td className="text-right font-mono font-semibold tabular-nums text-sm text-sage-700">
                      {r.completedCount}
                    </td>
                    <td className={`text-right font-mono font-semibold tabular-nums text-sm ${r.overdueCount > 0 ? 'text-danger' : 'text-ink-400'}`}>
                      {r.overdueCount}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-20 h-1.5 bg-ink-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${pct >= 80 ? 'bg-sage-500' : pct >= 50 ? 'bg-amber-500' : 'bg-danger'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="font-mono font-semibold text-xs tabular-nums text-ink-900 w-10 text-right">
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AssignmentStatusBadge({ status }: { status: string }) {
  const cls = {
    assigned:    'bg-canvas-subtle text-ink-700 ring-ink-200',
    in_progress: 'bg-blue-50 text-blue-700 ring-blue-200',
    completed:   'bg-sage-50 text-sage-700 ring-sage-200',
    overdue:     'bg-red-50 text-red-700 ring-red-200',
    cancelled:   'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function CreateCourseModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'compliance'|'technical'|'leadership'|'onboarding'|'other'>('technical');
  const [provider, setProvider] = useState('');
  const [deliveryMode, setDeliveryMode] = useState<'online'|'classroom'|'self_paced'|'blended'>('online');
  const [durationHours, setDurationHours] = useState<string>('');
  const [passingScore, setPassingScore] = useState<string>('');
  const [isMandatory, setIsMandatory] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.createCourse({
      code: code.trim() ? code.toUpperCase() : undefined, title,
      description: description || undefined,
      category, provider: provider || undefined,
      deliveryMode,
      durationHours: durationHours ? parseFloat(durationHours) : undefined,
      passingScore: passingScore ? parseFloat(passingScore) : undefined,
      isMandatory,
      externalUrl: externalUrl || undefined,
    }),
    onSuccess: () => { toast.success('Course created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · Training</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New course</h2>
        <p className="text-sm text-ink-500 mt-1">
          Add a training course to the catalogue. Mark mandatory to track it in compliance reports.
        </p>
      </div>

      <div className="px-6 py-5 grid grid-cols-2 gap-3 overflow-y-auto flex-1 min-h-0">
        <div>
          <label className="label">Code <span className="text-ink-400 font-normal">(auto if blank)</span></label>
          <input className="input font-mono uppercase" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Auto (CRS-0001)" />
        </div>
        <div>
          <label className="label">Category</label>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value as any)}>
            <option value="compliance">Compliance</option>
            <option value="technical">Technical</option>
            <option value="leadership">Leadership</option>
            <option value="onboarding">Onboarding</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="label">Title <span className="text-danger">*</span></label>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Information security awareness" />
        </div>
        <div className="col-span-2">
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <label className="label">Provider <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={provider} onChange={(e) => setProvider(e.target.value)}
            placeholder="internal, Coursera…" />
        </div>
        <div>
          <label className="label">Delivery mode</label>
          <select className="input" value={deliveryMode}
            onChange={(e) => setDeliveryMode(e.target.value as any)}>
            <option value="online">Online</option>
            <option value="classroom">Classroom</option>
            <option value="self_paced">Self paced</option>
            <option value="blended">Blended</option>
          </select>
        </div>
        <div>
          <label className="label">Duration (hours)</label>
          <input type="number" min={0} step={0.5} className="input tabular-nums"
            value={durationHours} onChange={(e) => setDurationHours(e.target.value)} />
        </div>
        <div>
          <label className="label">Passing score %</label>
          <input type="number" min={0} max={100} className="input tabular-nums"
            value={passingScore} onChange={(e) => setPassingScore(e.target.value)} placeholder="80" />
        </div>
        <div className="col-span-2">
          <label className="label">External URL <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://…" />
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
            <input type="checkbox" checked={isMandatory}
              onChange={(e) => setIsMandatory(e.target.checked)} />
            <span>Mandatory — tracked in compliance reports</span>
          </label>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!title || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create course'}
        </button>
      </div>
    </Modal>
  );
}

function AssignModal({ course, onClose, onAssigned }:
    { course: any; onClose: () => void; onAssigned: () => void }) {
  const empQ = useQuery({ queryKey: ['employees'], queryFn: () => hrmApi.listEmployees() });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.assignCourse({
      courseId: course.id,
      employeeIds: Array.from(selected),
      dueDate: dueDate || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => {
      toast.success(`${d.created} assigned${d.skipped > 0 ? `, ${d.skipped} skipped (already assigned)` : ''}`);
      onAssigned();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggleAll = () => {
    if (selected.size === empQ.data?.length) setSelected(new Set());
    else setSelected(new Set((empQ.data ?? []).map((e: any) => e.id)));
  };

  const toggle = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">People · Assign</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Assign {course.code}</h2>
        <p className="text-sm text-ink-500 mt-1">{course.title}</p>
      </div>

      <div className="px-6 pt-4 pb-3 grid grid-cols-2 gap-3 flex-shrink-0">
        <div>
          <label className="label">Due date <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="date" className="input" value={dueDate}
            onChange={(e) => setDueDate(e.target.value)} />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Visible to assignees" />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="flex justify-between items-baseline mb-2">
          <div className="eyebrow text-ink-500">
            Employees{selected.size > 0 && (
              <span className="ml-1.5 text-ink-700 font-mono tabular-nums">({selected.size} selected)</span>
            )}
          </div>
          <button type="button"
            className="text-xs font-semibold text-sage-700 hover:text-sage-800 transition-colors"
            onClick={toggleAll}>
            {selected.size === empQ.data?.length ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div className="border border-ink-100 rounded-xl max-h-80 overflow-y-auto">
          {empQ.isLoading ? <LoadingState rows={3} /> :
           empQ.error ? <ErrorState error={empQ.error} /> :
           !empQ.data?.length ? <EmptyState message="No employees." /> :
           (empQ.data ?? []).map((e: any) => (
            <label key={e.id}
              className="flex items-center gap-2 px-3 py-2 hover:bg-canvas-subtle text-sm border-b border-ink-100 last:border-b-0 cursor-pointer transition-colors">
              <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggle(e.id)} />
              <div>
                <div className="text-ink-900">{e.full_name}</div>
                <div className="text-2xs text-ink-500 font-mono">
                  {e.employee_code}{e.designation && <span className="mx-1 text-ink-300">·</span>}{e.designation}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={selected.size === 0 || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Assigning…' : `Assign to ${selected.size}`}
        </button>
      </div>
    </Modal>
  );
}

function CompleteAssignmentModal({ assignment, onClose, onCompleted }:
    { assignment: any; onClose: () => void; onCompleted: () => void }) {
  const hasPassingScore = assignment.passing_score !== null && assignment.passing_score !== undefined;
  const [score, setScore] = useState<string>('');
  const [certificateUrl, setCertificateUrl] = useState('');
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () => hrmApi.completeAssignment(assignment.id, {
      score: score ? parseFloat(score) : undefined,
      certificateUrl: certificateUrl || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (d) => {
      toast.success(d.passed ? `Completed · Passed${d.score ? ` with ${d.score}%` : ''}`
                              : `Completed · Failed with ${d.score}%`);
      onCompleted();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">People · Training</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Complete training</h2>
        <p className="text-sm text-ink-500 mt-1">{assignment.course_title}</p>
      </div>

      <div className="px-6 py-5 space-y-4">
        {hasPassingScore && (
          <div>
            <label className="label">
              Score 0–100 <span className="text-danger">*</span>
              <span className="font-normal text-ink-500 normal-case tracking-normal ml-1">
                — passing is {parseFloat(assignment.passing_score).toFixed(0)}%
              </span>
            </label>
            <input type="number" min={0} max={100}
              className="input tabular-nums" value={score}
              onChange={(e) => setScore(e.target.value)} />
          </div>
        )}
        <div>
          <label className="label">Certificate URL <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input font-mono" value={certificateUrl}
            onChange={(e) => setCertificateUrl(e.target.value)} placeholder="https://…" />
        </div>
        <div>
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary"
          disabled={(hasPassingScore && !score) || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Completing…' : 'Mark complete'}
        </button>
      </div>
    </Modal>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
